import { useEffect, useRef, useState } from "react";
import { Globe, Lock, CreditCard } from "lucide-react";
import { Button, Field, FormDialog, Icon, RecordList, Spinner, DialogCancel } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useCapability } from "../../app/bandContext.js";
import {
  useClubSettings,
  usePayoutStatus,
  useUpdateFanClubSettings,
  useUpdateClubTiers,
  useStartPayoutOnboarding,
} from "../../app/hooks/useFanClub.js";
import { ApiError, errorMessage } from "../../app/hooks/apiError.js";
import { problemType } from "../../app/hooks/useRelease.js";
import { ANTENNA_CLUB_URL } from "../../config/env.js";
import styles from "./FanClubSettingsModal.module.scss";

type FanClubTier = components["schemas"]["FanClubTier"];
type PayoutStatusView = components["schemas"]["PayoutStatusView"];

/** Up to 3 PAID tiers on top of the locked free tier (tier ladder).*/
const MAX_PAID_TIERS = 3;

/** A paid tier as the editor holds it — dollars as a string (parsed to cents on save), perks as newline text. */
interface PaidTierDraft {
  id: string;
  name: string;
  priceDollars: string;
  perks: string;
}

export interface FanClubSettingsModalProps {
  onClose: () => void;
}

/**
 * The Antenna fan-club settings modal (shot-42 "Fan club settings"; routed `/antenna/settings`).
 * The band's monetization config: the club name rename (PUT /antenna/settings), the Stripe payout status +
 * onboarding link (GET /antenna/payouts, POST /antenna/payouts/onboard), and the tier editor — a locked FREE
 * tier plus up to three PAID tiers (PUT /antenna/club).
 *
 * The two PUTs are NEVER optimistic: an explicit pending state, server confirmation, the server error
 * surfaced VERBATIM inline, and onClose only after `mutateAsync` resolves (a throw keeps the modal open). Both
 * carry the settings `version` (via `If-Match`) so a stale write surfaces 409 verbatim — the concurrency-safe
 * rename conflict — and a removed tier that still has members surfaces the **409 tier-has-members** verbatim,
 * never a silent retry. Authoring is `antenna:manage`; a read-only seat reaching the URL sees the forbidden
 * state, not the form (the client mirror of the server 403).
 */
export function FanClubSettingsModal({ onClose }: FanClubSettingsModalProps) {
  const canManage = useCapability("antenna:manage");
  const settings = useClubSettings();
  const payout = usePayoutStatus();
  const renameMut = useUpdateFanClubSettings();
  const tiersMut = useUpdateClubTiers();
  const onboardMut = useStartPayoutOnboarding();

  const [clubName, setClubName] = useState("");
  const [freeTier, setFreeTier] = useState<FanClubTier>({ id: "free", name: "Fan", priceCents: 0, perks: null });
  const [paidTiers, setPaidTiers] = useState<PaidTierDraft[]>([]);
  const [tiersDirty, setTiersDirty] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Set when an onboard press met the provider-guarded 502: the host LOOKED wired but Antenna could not mint
  // a link (its own Stripe account is absent until D-5's money switch). Same calm state, no error line.
  const [onboardUnavailable, setOnboardUnavailable] = useState(false);

  const nameRef = useRef<HTMLInputElement>(null);

  // Hydrate the form ONCE from the loaded settings (faithful name + tiers). A later cache change must not yank
  // the form out from under the author mid-edit (mirrors the composer's hydrate-once).
  useEffect(() => {
    if (hydrated) return;
    const v = settings.data;
    if (!v) return;
    setClubName(v.clubName ?? "");
    const tiers = v.tiers ?? [];
    const free = tiers.find((t) => (t.priceCents ?? 0) === 0) ?? { id: "free", name: "Fan", priceCents: 0, perks: null };
    setFreeTier(free);
    setPaidTiers(
      tiers
        .filter((t) => (t.priceCents ?? 0) > 0)
        .map((t) => ({
          id: t.id ?? crypto.randomUUID(),
          name: t.name ?? "",
          priceDollars: ((t.priceCents ?? 0) / 100).toString(),
          perks: (t.perks ?? []).join("\n"),
        })),
    );
    setHydrated(true);
  }, [hydrated, settings.data]);

  // `FormDialog` owns Escape, the focus trap and focus restore; it focuses its first focusable (the close
  // button), so claim the club name — the real entry point — once the form has hydrated and the field exists.
  useEffect(() => {
    if (hydrated) nameRef.current?.focus();
  }, [hydrated]);

  function addPaidTier() {
    setPaidTiers((rows) => [...rows, { id: crypto.randomUUID(), name: "", priceDollars: "", perks: "" }]);
    setTiersDirty(true);
  }

  function removePaidTier(index: number) {
    setPaidTiers((rows) => rows.filter((_, i) => i !== index));
    setTiersDirty(true);
  }

  // The paid tiers ride the PUT in array order, so a move re-sequences the ladder. Each tier keeps its id through
  // the move, so reordering never changes which members hold which tier.
  function movePaidTier(from: number, to: number) {
    setPaidTiers((rows) => {
      const next = [...rows];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setTiersDirty(true);
  }

  function patchPaidTier(id: string, patch: Partial<PaidTierDraft>) {
    setPaidTiers((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setTiersDirty(true);
  }

  function buildTiers(): FanClubTier[] {
    const paid: FanClubTier[] = paidTiers.map((t) => {
      const perks = t.perks.split("\n").map((p) => p.trim()).filter(Boolean);
      return {
        id: t.id,
        name: t.name.trim(),
        priceCents: Math.round(Number.parseFloat(t.priceDollars || "0") * 100),
        perks: perks.length ? perks : null,
      };
    });
    return [{ ...freeTier, priceCents: 0 }, ...paid];
  }

  function validateTiers(): boolean {
    for (const t of paidTiers) {
      if (!t.name.trim()) {
        setActionError("Give each paid tier a name before you save.");
        return false;
      }
      const price = Number.parseFloat(t.priceDollars || "0");
      if (!Number.isFinite(price) || price < 0) {
        setActionError(`Enter a valid price for “${t.name.trim() || "your paid tier"}”.`);
        return false;
      }
    }
    return true;
  }

  async function handleSave() {
    const normalizedName = clubName.trim() ? clubName.trim() : null;
    const nameChanged = normalizedName !== (settings.data?.clubName ?? null);
    if (!nameChanged && !tiersDirty) {
      onClose();
      return;
    }
    if (tiersDirty && !validateTiers()) return;

    setBusy(true);
    setActionError(null);
    try {
      let version = settings.data?.version ?? 0;
      if (nameChanged) {
        const v = await renameMut.mutateAsync({ clubName: normalizedName, version });
        version = v.version ?? version;
      }
      if (tiersDirty) {
        await tiersMut.mutateAsync({ tiers: buildTiers(), version });
      }
      onClose();
    } catch (err) {
      // Surface the server ProblemDetails verbatim — the 409 stale-version (rename) or 409 tier-has-members
      // (club) lands here; the modal stays open, nothing is silently retried.
      setActionError(errorMessage(err, "Couldn't save your fan club settings. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function handleOnboard() {
    setActionError(null);
    try {
      const link = await onboardMut.mutateAsync();
      // Open the Stripe Account Link the server minted. The URL is a runtime value (no literal → no host lint).
      if (link.url) window.open(link.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      if (err instanceof ApiError && err.status === 502 && problemType(err) === "provider-unavailable") {
        setOnboardUnavailable(true);
        return;
      }
      setActionError(errorMessage(err, "Couldn't open Stripe onboarding. Please try again."));
    }
  }

  const payoutView = payoutRowState(payout.data, onboardUnavailable);

  const formReady = canManage && !settings.isError && hydrated;

  return (
    <FormDialog
      title="Fan club settings"
      closeLabel="Close fan club settings"
      size="lg"
      onClose={onClose}
      footer={
        formReady ? (
          <>
            <DialogCancel disabled={busy} />
            <Button variant="accent" onClick={() => void handleSave()} loading={busy} disabled={busy}>
              Save changes
            </Button>
          </>
        ) : undefined
      }
    >
      {!canManage ? (
        <div className={styles.notice} role="note">
          <p className={styles.noticeTitle}>Not available for your role</p>
          <p className={styles.noticeText}>
            Your role on this band doesn&apos;t include managing the fan club.
          </p>
          <Button variant="ghost" onClick={onClose}>
            Back to fan club
          </Button>
        </div>
      ) : settings.isError ? (
        <div className={styles.notice} role="note">
          <p className={styles.noticeTitle}>Couldn&apos;t load your fan club</p>
          <p className={styles.noticeText}>{settings.error?.message ?? "Please try again."}</p>
          <Button variant="ghost" onClick={() => void settings.refetch()}>
            Retry
          </Button>
        </div>
      ) : !hydrated ? (
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner size={22} label="Loading fan club settings…" />
          <p className={styles.centerText}>Loading fan club settings…</p>
        </div>
      ) : (
        <>
          {/* CLUB NAME — the rename (PUT /antenna/settings). */}
          <section className={styles.section}>
            <Field
              ref={nameRef}
              label="Club name"
              placeholder="e.g. The Freight Yard"
              value={clubName}
              onChange={(e) => setClubName(e.target.value)}
            />
          </section>

          {/* ANTENNA URL. The live public club URL from the band's vanity handle
              (FanClubSettingsView.handle) + the config-injected antennacntrl.app origin (ANTENNA_CLUB_URL,
              never a compiled-in host). Until a handle is set, the born-blank copy stands. */}
          <section className={styles.section}>
            <p className={styles.eyebrow}>Public page</p>
            <div className={styles.urlRow}>
              <Icon icon={Globe} size={16} />
              {settings.data?.handle ? (
                <span>{`${ANTENNA_CLUB_URL}/${settings.data.handle}`}</span>
              ) : (
                <span>Your public club page goes live once your Antenna handle is set.</span>
              )}
            </div>
          </section>

          {/* PAYOUTS — Stripe connect status + onboarding link. Driven by the status read: until this
              host can reach Antenna's onboarding (`onboardingAvailable`, rulings Q-5) a band with no account
              sees a calm "open soon" line with NO button, rather than one whose only outcome is a 502. */}
          <section className={styles.section}>
            <p className={styles.eyebrow}>Payouts</p>
            {payout.isLoading ? (
              <p className={styles.payoutSub} role="status">
                Checking your payout status…
              </p>
            ) : payout.isError ? (
              <div className={styles.payoutRow}>
                <p className={styles.payoutSub}>Couldn&apos;t load your payout status.</p>
                <Button variant="ghost" size="sm" onClick={() => void payout.refetch()}>
                  Retry
                </Button>
              </div>
            ) : (
              <div className={styles.payoutRow}>
                <div className={styles.payoutInfo}>
                  <span className={styles.payoutBadge} data-tone={payoutView.tone}>
                    {payoutView.label}
                  </span>
                  <p className={styles.payoutSub}>{payoutView.sub}</p>
                </div>
                {payoutView.canOnboard && (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={onboardMut.isPending}
                    disabled={onboardMut.isPending}
                    onClick={() => void handleOnboard()}
                  >
                    <Icon icon={CreditCard} size={16} /> Update details on Stripe
                  </Button>
                )}
              </div>
            )}
          </section>

          {/* TIER EDITOR — the locked free tier, then up to 3 paid tiers as a RecordList (PUT /antenna/club). */}
          <section className={styles.section}>
            <p className={styles.eyebrow}>Membership tiers</p>
            <div className={styles.freeTier}>
              <div className={styles.tierHead}>
                <span className={styles.tierBadge}>Free</span>
                <span className={styles.priceLocked}>
                  <Icon icon={Lock} size={16} /> $0
                </span>
              </div>
              <p className={styles.sectionHint}>
                {freeTier.name || "Fan"} — everyone who joins your club starts here.
              </p>
            </div>

            <RecordList
              dragKind="fan-club-paid-tier"
              items={paidTiers}
              layout="stack"
              max={MAX_PAID_TIERS}
              showCount
              addLabel="Add paid tier"
              nameOf={(t) => t.name.trim() || "paid tier"}
              onAdd={addPaidTier}
              onMove={movePaidTier}
              onRemove={removePaidTier}
              renderFields={(t) => (
                <>
                  <div className={styles.tierGrid}>
                    <Field
                      label="Tier name"
                      placeholder="e.g. Backstage"
                      value={t.name}
                      onChange={(e) => patchPaidTier(t.id, { name: e.target.value })}
                    />
                    <Field
                      label="Price ($/mo)"
                      inputMode="decimal"
                      placeholder="5"
                      value={t.priceDollars}
                      onChange={(e) => patchPaidTier(t.id, { priceDollars: e.target.value })}
                    />
                  </div>
                  <div>
                    <label htmlFor={`perks-${t.id}`} className={styles.perksLabel}>
                      Perks (one per line)
                    </label>
                    <textarea
                      id={`perks-${t.id}`}
                      className={styles.perksArea}
                      rows={2}
                      placeholder={"Early demos\nBackstage photos"}
                      value={t.perks}
                      onChange={(e) => patchPaidTier(t.id, { perks: e.target.value })}
                    />
                  </div>
                </>
              )}
            />
          </section>

          {actionError && (
            <p className={styles.error} role="alert">
              {actionError}
            </p>
          )}
        </>
      )}
    </FormDialog>
  );
}

/**
 * Map the Stripe payout status to the row: badge tone + copy, and whether the onboarding button is offered.
 *
 * Two different absences, kept apart: "you have not connected Stripe" (theirs — the button) and "nobody can
 * connect here yet" (ours — `onboardingAvailable` false, or an onboard press that met the provider 502). The
 * second is only the answer for a band with NO account: a band that connected earlier keeps its real status
 * and loses just the button, since the link it opens cannot be minted (the connections tab's same split).
 * An absent `onboardingAvailable` (an older server) fails closed to "open soon", never to a dead button.
 */
function payoutRowState(
  status: PayoutStatusView | undefined,
  onboardUnavailable: boolean,
): { tone: "success" | "warning" | "neutral"; label: string; sub: string; canOnboard: boolean } {
  const canOnboard = status?.onboardingAvailable === true && !onboardUnavailable;
  if (status?.payoutsEnabled) {
    return { tone: "success", label: "Payouts enabled", sub: "You're all set to get paid.", canOnboard };
  }
  if (status?.detailsSubmitted) {
    return {
      tone: "warning",
      label: "Under review",
      sub: "Stripe is finishing verifying your details.",
      canOnboard,
    };
  }
  if (!canOnboard && !status?.stripeAccountId) {
    return {
      tone: "neutral",
      label: "Payouts open soon",
      sub: "Stripe payouts for paid memberships switch on here soon. There's nothing to set up yet.",
      canOnboard: false,
    };
  }
  return {
    tone: "neutral",
    label: "Not connected",
    sub: "Connect Stripe to start taking paid memberships.",
    canOnboard,
  };
}
